import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition, RoomOptions } from '@/game/roomModule';
import { getZone } from '@/render/palette';
import {
  CULL_RETURN_SPAWN, CULL_SPAWN, CULL_ZONE_ID, createCullCorridor, cullCorridorAmbience, cullCorridorFixtures, cullCorridorSolids,
  type CullCorridorParts,
} from '@/world/cullCorridor';

/**
 * Beat 5's cull corridor, and beat 8's walk back through it, as a room
 * module. Until session 82 it was a construction branch in `main.ts`.
 *
 * **The room is static.** The machine's cycle, the sheeting's lift and the
 * bin that carries her out are beat 5's (`CullDirector`, still wired in
 * `main.ts`), which reaches them through `RoomModule.cullCorridor`. Beat 8's
 * return is the relit room with none of that in it: no director inherits the
 * Act 1 machine (`BLACKOUT_RETURN_PART_MODULE`).
 *
 * It declares no `stealth`, unlike beat 4's corridor: nothing in this room is
 * looking for her (`docs/beat-5-plan.md` rule 1).
 *
 * `?dress=0` is not honoured, as the legacy branch never honoured it.
 */

/** Beat 8, part 1: the corridor relit, after Act 1. */
function isReturn(options: RoomOptions): boolean {
  return options.beat === 8 && options.part === 1;
}

function cullEnvironment(options: RoomOptions): RoomEnvironment {
  const zone = getZone(CULL_ZONE_ID);
  if (zone === undefined) throw new Error(`unknown zone "${CULL_ZONE_ID}"`);
  const returning = isReturn(options);
  return {
    zone,
    lighting: {
      fixtures: cullCorridorFixtures(returning ? { returnState: true } : {}),
      // Steeper and weaker than the service corridor's. This room's contact
      // shadows come from the bay's spill and the red lamp, both of which are
      // point lights at one end of a long space, so a strong key would flatten
      // the one thing the room is made of — a lit slot in a dark run.
      keyDirection: { x: -0.2, y: 0.94, z: 0.28 },
      keyIntensity: 0.16,
      // The corridor is 3.2 x 29 with a bay off it. One extent covers both.
      shadowExtent: 22,
      // **Mean, and it is the opposite call from beat 4's.** D-081 gave the
      // service corridor a generous ambient because it has no dark and its
      // fiction says so. This room's fiction is the other one: the middle third
      // has no fitting at all, and an ambient that lifted it would light the
      // part of the walk that is supposed to be lit by one red lamp twelve
      // metres away.
      hemiIntensity: 0.17,
      ambientIntensity: 0.09,
      // The bay spill is intensity 11 and the red lamp 9, both point lights, so
      // the ramp is anchored above the corridor's own level — but only just.
      // 1.7 was the first guess and the first frames came back a black
      // rectangle with a slightly less black rectangle in it; the room's own
      // surfaces sit near the bottom of the ramp by design and an anchor that
      // high put them under it.
      exposureReference: 1.05,
    },
    background: { kind: 'rig' },
    spawn: returning ? CULL_RETURN_SPAWN : CULL_SPAWN,
    // The return runs no machinery, so it declares none: the hall hum alone.
    // The legacy branch skipped `setAmbience` here instead, which left the
    // engine's DEFAULT beds playing — the grey-box room's fan, buzz and pump,
    // placed outside the farrowing hall's walls (D-227).
    ambience: returning ? [] : cullCorridorAmbience(),
  };
}

function createCullRoom(options: RoomOptions, capabilities: RoomCapabilities): RoomModule {
  const returning = isReturn(options);
  const parts: CullCorridorParts | null = returning ? null : createCullCorridor();
  const group = parts?.group ?? createCullCorridor({ returnState: true }).group;
  capabilities.scene.add(group);
  const colliders: ColliderHandle[] = cullCorridorSolids(returning ? { returnState: true } : {})
    .map((solid) => capabilities.colliders.add(solid));
  let disposed = false;
  return {
    interactables: [],
    fixedStep(): void { /* static: beat 5's director owns everything that moves */ },
    renderStep(): void { /* static */ },
    framing: () => null,
    diagnostics: () => ({ kind: 'none' }),
    ...(parts === null ? {} : { cullCorridor: parts }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of colliders) capabilities.colliders.remove(handle);
      capabilities.scene.remove(group);
    },
  };
}

export const CULL_ROOM_MODULE: RoomModuleDefinition = {
  room: 'cull',
  environment: cullEnvironment,
  create: (options, capabilities) => createCullRoom(options, capabilities),
};
